import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ContactService from "services/Contact";
import TagService from "services/Tag";
import CustomFieldService from "services/CustomField";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns } from "./useTableConfig";
import {
  getScheduleViewContactsTableColumns,
  getScheduleViewContactsTableFilters,
} from "routes/campaign/components/email_schedule_contacts_table/ScheduleViewContactsTableColumnsAndFilters";
import { SCHEDULE_VIEW_CONTACTS_TABLE_CONSTANTS } from "routes/campaign/components/email_schedule_contacts_table/ScheduleViewContactsTableV3";
import { scheduleViewContactKeys } from "routes/campaign/components/email_schedule_contacts_table/queryKeys";

export const handleFetchTags = async () => {
  return await TagService.getTags({
    model: SCHEDULE_VIEW_CONTACTS_TABLE_CONSTANTS.tagModel,
    for_filtering: true,
  });
};

const handleFetchCustomFields = async () => {
  const customFields = await ContactService.getCustomFields();
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel("Contact");

  return [
    {
      accessorPrefix: "custom_fields",
      fields: customFields,
      model: "Contact",
      headerPrefix: "Contact",
      configs,
    },
  ];
};

export const useScheduleViewContactsTable = ({
  campaignId = null,
  data = null,
}) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", SCHEDULE_VIEW_CONTACTS_TABLE_CONSTANTS.className],
    queryFn: handleFetchTags,
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: [
      "custom-fields",
      SCHEDULE_VIEW_CONTACTS_TABLE_CONSTANTS.className,
    ],
    queryFn: handleFetchCustomFields,
  });

  const queryKey = useMemo(
    () => scheduleViewContactKeys.list({ campaignId, data }),
    [campaignId, data]
  );

  const filters = useMemo(() => {
    if (!isTagsFetched || !isCustomFieldsFetched) {
      return [];
    }
    const finalFilters = getScheduleViewContactsTableFilters({ tags });

    finalFilters.push(
      ...buildCustomFieldFilters(
        customFields,
        finalFilters.map(f => f.title)
      )
    );

    return finalFilters;
  }, [tags, customFields, isTagsFetched, isCustomFieldsFetched]);

  const columns = useMemo(() => {
    if (!isCustomFieldsFetched) return [];
    const initialColumns = getScheduleViewContactsTableColumns();

    const allCustomFieldIds = customFields.flatMap(cf =>
      cf.fields.map(field => `${cf.accessorPrefix}.${field}`)
    );

    const customFieldColumns = addCustomFieldsColumns({
      columns: [],
      visibleColumns: allCustomFieldIds,
      customFields,
    }).map(col => ({ ...col, enableSorting: false }));

    return [...initialColumns, ...customFieldColumns];
  }, [customFields, isCustomFieldsFetched]);

  return { filters, columns, tags, customFields, queryKey };
};
